// =============================================================================
// DefectCentreScreen — /acceptance/defects (HOP Slice 4)
// The cross-project defect register. See what's open and who's on it, filter by
// status, raise new defects, and move a defect through its lifecycle (open →
// in progress → awaiting re-inspection → closed) with an audited status change.
// Independent of pack acceptance — a project can progress with defects open.
// =============================================================================

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle, Plus, ArrowLeft, Wrench, CheckCircle2, Clock, ClipboardList,
  User as UserIcon, CalendarClock, FolderKanban,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, KPI, Spinner, EmptyState, Button } from "@/components/ui";
import { format, parseISO } from "date-fns";
import RaiseDefectModal from "./RaiseDefectModal";
import DefectDetailModal from "./DefectDetailModal";

type Status = "open" | "in_progress" | "awaiting_reinspection" | "closed" | "cancelled";

const STATUS_LABEL: Record<Status, string> = {
  open: "Open", in_progress: "In progress", awaiting_reinspection: "Awaiting re-inspection", closed: "Closed", cancelled: "Cancelled",
};
const STATUS_TONE: Record<Status, any> = {
  open: "critical", in_progress: "pending", awaiting_reinspection: "notice", closed: "approved", cancelled: "default",
};
const SEV_TONE: Record<string, any> = { minor: "notice", major: "warning", critical: "critical" };
const NEXT: Record<Status, Status[]> = {
  open: ["in_progress", "cancelled"],
  in_progress: ["awaiting_reinspection", "closed"],
  awaiting_reinspection: ["closed", "in_progress"],
  closed: ["in_progress"],
  cancelled: ["open"],
};

function isOverdue(d: any): boolean {
  return d.status !== "closed" && d.status !== "cancelled" && d.due_date && new Date(d.due_date + "T23:59:59").getTime() < Date.now();
}

export default function DefectCentreScreen() {
  const user = useRequireAuth();
  const canManage = hasRole(user, "manager");
  const [tab, setTab] = useState<"open" | "all" | "closed">("open");
  const [raise, setRaise] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: defects = [], isLoading, refetch } = useQuery({
    queryKey: ["site_defects"],
    queryFn: async () => {
      const { data, error } = await supabase.from("site_defects").select(`
        defect_id, defect_number, category, severity, title, description, status,
        due_date, raised_at, closed_at, source,
        project:project_id ( project_id, project_code, project_name ),
        site:site_id ( site_name ),
        pack:pack_id ( pack_number ),
        assignee:assigned_to_employee_id ( first_name, last_name )
      `).order("raised_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const counts = useMemo(() => {
    const open = defects.filter(d => d.status !== "closed" && d.status !== "cancelled");
    return {
      open: open.length,
      overdue: open.filter(isOverdue).length,
      critical: open.filter(d => d.severity === "critical").length,
      closed30: defects.filter(d => d.status === "closed" && d.closed_at && (Date.now() - new Date(d.closed_at).getTime()) < 30 * 864e5).length,
    };
  }, [defects]);

  const filtered = useMemo(() => {
    if (tab === "all") return defects;
    if (tab === "closed") return defects.filter(d => d.status === "closed" || d.status === "cancelled");
    return defects.filter(d => d.status !== "closed" && d.status !== "cancelled");
  }, [defects, tab]);

  async function setStatus(d: any, next: Status) {
    const patch: any = { status: next };
    if (next === "closed") { patch.closed_at = new Date().toISOString(); patch.closed_by = user?.user_id ?? null; }
    if (d.status === "closed" && next !== "closed") { patch.closed_at = null; patch.closed_by = null; }
    await supabase.from("site_defects").update(patch).eq("defect_id", d.defect_id);
    await supabase.from("defect_status_history").insert({ defect_id: d.defect_id, changed_by: user?.user_id ?? null, old_status: d.status, new_status: next });
    refetch();
  }

  if (!user) return null;

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto pb-20">
      <Link to="/acceptance" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3"><ArrowLeft size={14} /> Acceptance Centre</Link>

      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2"><Wrench size={20} className="text-accent" /> Defect Register</h1>
          <p className="text-text-muted text-[13px] mt-1">Open site issues we have to go back for — who's on it, the ETA, and status. Tracked beside the job; never blocks a project.</p>
        </div>
        {canManage && <Button icon={Plus} onClick={() => setRaise(true)}>Raise defect</Button>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPI label="Open" value={counts.open} icon={ClipboardList} accent={counts.open > 0} />
        <KPI label="Overdue" value={counts.overdue} icon={CalendarClock} accent={counts.overdue > 0} sub="past ETA" />
        <KPI label="Critical open" value={counts.critical} icon={AlertTriangle} accent={counts.critical > 0} />
        <KPI label="Closed" value={counts.closed30} icon={CheckCircle2} sub="last 30 days" />
      </div>

      <div className="mb-4 flex items-center gap-1 p-1 bg-ink-raised border border-line rounded-md w-fit">
        {(["open", "all", "closed"] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded text-[12px] capitalize ${tab === t ? "bg-accent/15 text-accent border border-accent/30" : "text-text-muted hover:text-text border border-transparent"}`}>{t}</button>
        ))}
      </div>

      {isLoading ? (
        <div className="p-12 flex justify-center"><Spinner size={20} /></div>
      ) : filtered.length === 0 ? (
        <Card><EmptyState icon={CheckCircle2} title={tab === "open" ? "No open defects" : "Nothing here"} description={tab === "open" ? "Clean — nothing waiting to go back for." : "No defects match this filter."} /></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(d => {
            const overdue = isOverdue(d);
            return (
              <Card key={d.defect_id}>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setOpenId(d.defect_id)}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[12px] text-accent">{d.defect_number}</span>
                        <Pill label={STATUS_LABEL[d.status as Status]} tone={STATUS_TONE[d.status as Status]} small />
                        <Pill label={d.severity} tone={SEV_TONE[d.severity] ?? "default"} small />
                        <span className="text-[11px] text-text-muted capitalize">{d.category}</span>
                        {d.source === "client" && <Pill label="client-raised" tone="bid" small />}
                        {overdue && <span className="text-[11px] text-danger font-medium">· overdue</span>}
                      </div>
                      <div className="text-[14px] text-text mt-1">{d.title || d.description}</div>
                      {d.title && <div className="text-[12px] text-text-muted mt-0.5 line-clamp-2">{d.description}</div>}
                      <div className="text-[11px] text-text-muted mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="inline-flex items-center gap-1 font-mono"><FolderKanban size={11} /> {d.project?.project_code ?? "—"}{d.site?.site_name ? ` · ${d.site.site_name}` : ""}</span>
                        {d.pack?.pack_number && <span className="inline-flex items-center gap-1">{d.pack.pack_number}</span>}
                        {d.assignee && <span className="inline-flex items-center gap-1"><UserIcon size={11} /> {d.assignee.first_name} {d.assignee.last_name}</span>}
                        {d.due_date && <span className={`inline-flex items-center gap-1 ${overdue ? "text-danger" : ""}`}><Clock size={11} /> ETA {format(parseISO(d.due_date), "dd MMM")}</span>}
                        <span>raised {format(parseISO(d.raised_at), "dd MMM")}</span>
                      </div>
                    </div>
                    {canManage && (
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {NEXT[d.status as Status]?.map(ns => (
                          <button key={ns} onClick={() => setStatus(d, ns)}
                            className={`text-[11px] px-2 py-1 rounded border transition-colors ${ns === "closed" ? "border-ok/40 text-ok hover:bg-ok/10" : "border-line text-text-muted hover:border-accent hover:text-accent"}`}>
                            {ns === "closed" ? "Close" : STATUS_LABEL[ns]}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {raise && <RaiseDefectModal onClose={() => setRaise(false)} onCreated={() => refetch()} />}
      {openId && <DefectDetailModal defectId={openId} onClose={() => setOpenId(null)} onChanged={() => refetch()} />}
    </div>
  );
}
